import React, { useCallback, useMemo, useState } from 'react';
import { List } from 'immutable';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { selectExtensions } from 'flow/selectors/ExtensionSelectors';

import useAppContext from 'flow/hooks/useAppContext';
import { sortByName } from 'flow/utils';
import {
    filterExtensionsBySearchableInfo,
    getFixedNumberFromContact,
    getMobileNumberFromContact,
} from 'flow/utils/extension-utils';
import { twMerge } from 'tailwind-merge';
import BaseModal from 'flow/components/ui-components/BaseModal';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Divider from 'flow/components/ui-components/Divider';
import IconButton from 'flow/components/ui-components/IconButton';
import Icon from 'flow/components/ui-components/Icon';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';

import { TypedMap } from 'flow/types/TypedMap';

import FilterSearchBar from 'flow/components/FilterSearchBar';
import ExtensionSelectable from 'flow/components/tickets/ticket-details/AddMembers/ExtensionSelectable';

interface Props {
    agentsToExclude: List<ExtensionKey>;
    maximumAmount: number;
    onClickAdd: (keys: List<ExtensionKey>) => void;
    onClose: () => void;
}

const AddAgentsModal = ({
    agentsToExclude = List(),
    maximumAmount,
    onClose,
    onClickAdd,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const extensions = useAppSelector(selectExtensions);

    const [filterText, setFilterText] = useState('');
    const [extensionKeysSelected, setExtensionKeysSelected] =
        useState<List<ExtensionKey>>(List());

    const extensionsToDisplayInSelectableList = useMemo(() => {
        return extensions.filter(
            (extension: TypedMap<Extension>) =>
                !agentsToExclude.contains(extension.get('key')) &&
                !extensionKeysSelected.contains(extension.get('key')),
        );
    }, [extensions, extensionKeysSelected, agentsToExclude]);

    const onClickSubmit = useCallback(() => {
        onClickAdd(extensionKeysSelected);
        onClose();
    }, [dispatch, onClose, onClickAdd, extensionKeysSelected]);

    const onClickSelect = useCallback(
        (extensionKey: ExtensionKey) => {
            setExtensionKeysSelected(extensionKeysSelected.push(extensionKey));
            setFilterText('');
        },
        [setExtensionKeysSelected, extensionKeysSelected, setFilterText],
    );

    const onClickDeselect = useCallback(
        (extensionKey: ExtensionKey) => {
            const deselectedIndex = extensionKeysSelected.findIndex(
                (key) => key === extensionKey,
            );
            setExtensionKeysSelected(
                extensionKeysSelected.remove(deselectedIndex),
            );
        },
        [extensionKeysSelected, setExtensionKeysSelected],
    );

    return (
        <BaseModal
            open={true}
            header={i18next.t('wallboard.agents.title')}
            onClose={onClose}
            confirmButtonText={i18next.t(
                'wallboard.agents.add_users_to_dashboard',
            )}
            className="w-[600px]"
            onSubmit={onClickSubmit}
            disablePadding={true}
        >
            <div className="flex flex-col gap-3 ps-6 pe-4">
                <FilterSearchBar
                    searchText={filterText}
                    placeholder={i18next.t('input.filter')}
                    handleFilter={(event, value) => {
                        setFilterText(value);
                    }}
                />

                {!extensionKeysSelected.isEmpty() ? (
                    <div className="flex flex-wrap gap-3">
                        {extensionKeysSelected
                            .map((extensionKey) => {
                                const extension = extensions.get(extensionKey);
                                return (
                                    <div
                                        key={extension.get('key')}
                                        className="flex items-center rounded-sm border border-gray-300 p-1"
                                    >
                                        <ContactAvatar
                                            contact={extension.get('contact')}
                                            size="small"
                                            fallBackBackgroundColorClass={twMerge(
                                                'group-hover:bg-gray-300 bg-gray-200',
                                            )}
                                        />
                                        <div className="ms-2 me-3 text-sm">
                                            {extension.getIn(
                                                ['contact', 'firstName'],
                                                '',
                                            ) ||
                                                getFixedNumberFromContact(
                                                    extension.get('contact'),
                                                ) ||
                                                getMobileNumberFromContact(
                                                    extension.get('contact'),
                                                ) ||
                                                null}
                                        </div>
                                        <div>
                                            <IconButton
                                                aria-label={i18next.t('remove')}
                                                className="size-6"
                                                onClick={() =>
                                                    onClickDeselect(
                                                        extensionKey,
                                                    )
                                                }
                                            >
                                                <Icon iconKey="cancel" />
                                            </IconButton>
                                        </div>
                                    </div>
                                );
                            })
                            .valueSeq()}
                    </div>
                ) : null}
                <Divider />

                <div className="h-[22.5rem] overflow-y-scroll">
                    {!extensionsToDisplayInSelectableList.isEmpty()
                        ? filterExtensionsBySearchableInfo(
                              extensionsToDisplayInSelectableList.sort(
                                  sortByName,
                              ),
                              filterText,
                          )
                              .map((extension: TypedMap<Extension>) => (
                                  <ExtensionSelectable
                                      key={`selectable-${extension.get('key')}`}
                                      extensionKey={extension.get('key')}
                                      onClickAdd={onClickSelect}
                                      disabled={
                                          extensionKeysSelected.size ===
                                          maximumAmount
                                      }
                                  />
                              ))
                              .valueSeq()
                        : null}
                </div>
            </div>
        </BaseModal>
    );
};

export default AddAgentsModal;
